import React, { useState, useMemo, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Search, ArrowRight, DollarSign, Gamepad2, BarChart3, LineChart, FileText, ShieldCheck, Clock, Brain, TrendingDown, Swords, Gift, Sparkles, Flame, Heart, ShieldAlert, Trophy, Timer, Compass } from 'lucide-react';

const CATEGORIES = [
  { key: 'all', label: 'All Tools', color: '#a0a8b8' },
  { key: 'play-smart', label: 'Play Smart', color: '#4d7cfe' },
  { key: 'viral', label: 'Viral Tools', color: '#8b5cf6' },
  { key: 'wellness', label: 'Emotional Wellness', color: '#10b981' },
];

const TOOLS = [
  // Play Smart (7)
  { name: 'Gambling Budget Calculator', desc: 'Calculate your safe monthly gambling budget based on real income analysis', path: '/play-smart/gambling-budget-calculator', icon: DollarSign, category: 'play-smart', color: '#4d7cfe', uses: '12,450', badge: 'Most Popular' },
  { name: 'Cost2Play Calculator', desc: 'See exactly how much each game costs you over time', path: '/play-smart/cost2play-calculator', icon: Gamepad2, category: 'play-smart', color: '#8b5cf6', uses: '8,920' },
  { name: 'RTP Comparison Engine', desc: 'Compare Return-to-Player rates across 5,000+ slot games', path: '/play-smart/rtp-comparison-engine', icon: BarChart3, category: 'play-smart', color: '#10b981', uses: '15,300', badge: 'Trending' },
  { name: 'Bankroll Simulator', desc: 'Monte Carlo simulation showing how your bankroll changes over 200 runs', path: '/play-smart/bankroll-simulator', icon: LineChart, category: 'play-smart', color: '#4d7cfe', uses: '6,780' },
  { name: 'Bonus Terms Decoder', desc: 'Decode complex casino bonus terms into plain language with color-coded flags', path: '/play-smart/bonus-terms-decoder', icon: FileText, category: 'play-smart', color: '#f59e0b', uses: '9,150' },
  { name: 'Pre-Deposit Checklist', desc: 'Automated casino safety checker with 10-point verification before depositing', path: '/play-smart/pre-deposit-checklist', icon: ShieldCheck, category: 'play-smart', color: '#10b981', uses: '7,340' },
  { name: 'Withdrawal Predictor', desc: 'Estimate real-world withdrawal time based on casino and payment method', path: '/play-smart/withdrawal-predictor', icon: Clock, category: 'play-smart', color: '#8b5cf6', uses: '5,620' },
  // Viral Tools (6)
  { name: 'Gambler Personality Quiz', desc: 'Discover your gambler archetype with 10 questions and get personalized game recommendations', path: '/viral-tools/gambler-personality-quiz', icon: Brain, category: 'viral', color: '#4d7cfe', uses: '50,234', badge: 'Most Shared' },
  { name: 'Win Prediction Simulator', desc: 'Realistic annual outcome simulator — your gambling reality check', path: '/viral-tools/win-prediction-simulator', icon: TrendingDown, category: 'viral', color: '#ef4444', uses: '11,870' },
  { name: 'Friend Challenge Cards', desc: '10 timed casino IQ questions — challenge your friends to beat your score', path: '/viral-tools/friend-challenge', icon: Swords, category: 'viral', color: '#8b5cf6', uses: '23,100' },
  { name: 'Spin to Win', desc: 'Spin the wheel to unlock exclusive casino bonus codes', path: '/viral-tools/spin-to-win', icon: Gift, category: 'viral', color: '#f59e0b', uses: '34,500', badge: 'Fun' },
  { name: 'Slot Volatility Matcher', desc: 'Find your perfect slot matches based on your playing style', path: '/viral-tools/slot-volatility-matcher', icon: Sparkles, category: 'viral', color: '#10b981', uses: '18,900' },
  { name: 'Betting Streak Tracker', desc: 'Track wins, losses, and discover your patterns over time', path: '/viral-tools/betting-streak-tracker', icon: Flame, category: 'viral', color: '#f59e0b', uses: '14,200' },
  // Emotional Wellness (6)
  { name: 'Mood-Before-Play Tracker', desc: 'Pre-session emotional check-in with risk scoring and pattern analysis', path: '/emotional-wellness/mood-tracker', icon: Heart, category: 'wellness', color: '#ef4444', uses: '8,450' },
  { name: 'Loss-Chase Detector', desc: 'AI pattern recognition detecting escalating behavior with interventions', path: '/emotional-wellness/loss-chase-detector', icon: ShieldAlert, category: 'wellness', color: '#ef4444', uses: '6,300' },
  { name: 'Win Celebration Generator', desc: 'Celebrate your wins smartly with shareable victory cards and money management tips', path: '/emotional-wellness/win-celebration', icon: Trophy, category: 'wellness', color: '#f59e0b', uses: '4,200' },
  { name: 'Session Timer', desc: 'Real-time session timer with periodic emotional check-ins and loss limit protection', path: '/emotional-wellness/session-timer', icon: Timer, category: 'wellness', color: '#4d7cfe', uses: '5,100' },
  { name: 'Alternative Activities', desc: 'Healthy alternatives when the urge to gamble hits — 24 activities across 6 categories', path: '/emotional-wellness/alternatives', icon: Compass, category: 'wellness', color: '#06b6d4', uses: '3,800' },
  { name: 'Regret Anticipation Tool', desc: 'Pre-deposit reflection that visualizes future regret to prevent impulsive decisions', path: '/emotional-wellness/regret-anticipation', icon: ShieldAlert, category: 'wellness', color: '#8b5cf6', uses: '2,900' },
];

const CAT_DESCRIPTIONS = {
  'play-smart': { count: 7, tagline: '7 tools for data-driven decisions' },
  'viral': { count: 6, tagline: '6 tools for challenges and fun' },
  'wellness': { count: 6, tagline: '6 tools for responsible play' },
};

export default function ToolDiscovery() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const initialCat = searchParams.get('category') || 'all';
  const [activeCategory, setActiveCategory] = useState(initialCat);

  useEffect(() => {
    const cat = searchParams.get('category');
    if (cat) setActiveCategory(cat);
  }, [searchParams]);

  const filtered = useMemo(() => {
    return TOOLS.filter(t => {
      const matchesCat = activeCategory === 'all' || t.category === activeCategory;
      const matchesSearch = !search || t.name.toLowerCase().includes(search.toLowerCase()) || t.desc.toLowerCase().includes(search.toLowerCase());
      return matchesCat && matchesSearch;
    });
  }, [search, activeCategory]);

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="tool-discovery-page">
      {/* Hero */}
      <div className="text-center mb-10 animate-fade-in">
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3" style={{ letterSpacing: '-0.02em' }}>
          19 Smart Tools for Intelligent Players
        </h1>
        <p className="text-[#a0a8b8] text-base lg:text-lg max-w-2xl mx-auto">
          Calculators, strategies, and wellness tools to play smarter and stay in control
        </p>
      </div>

      {/* Search */}
      <div className="max-w-md mx-auto mb-8">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#64748b]" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search tools..."
            className="w-full h-12 pl-11 pr-4 rounded-xl bg-[#1a1f2e] border border-white/10 text-white text-sm placeholder:text-[#64748b] focus:outline-none focus:border-[#4d7cfe]/40 transition-colors"
            data-testid="tool-search-input"
          />
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap items-center justify-center gap-2 mb-2">
        {CATEGORIES.map(cat => (
          <button key={cat.key} onClick={() => setActiveCategory(cat.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeCategory === cat.key ? 'text-white' : 'text-[#64748b] hover:text-[#a0a8b8]'}`}
            style={activeCategory === cat.key ? { background: `${cat.color}18`, color: cat.color, border: `1px solid ${cat.color}30` } : { border: '1px solid transparent' }}
            data-testid={`filter-${cat.key}`}>
            {cat.label}
          </button>
        ))}
      </div>
      <p className="text-center text-xs text-[#64748b] mb-8" data-testid="tool-count">Showing {filtered.length} tool{filtered.length !== 1 ? 's' : ''}</p>

      {/* Tool Grid */}
      {filtered.length === 0 ? (
        <div className="text-center py-16">
          <Search className="h-10 w-10 text-[#3a4155] mx-auto mb-3" />
          <p className="text-[#64748b] text-sm">No tools match your search</p>
          <button onClick={() => { setSearch(''); setActiveCategory('all'); }} className="text-[#4d7cfe] text-sm mt-2 hover:underline">Clear filters</button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((tool, i) => {
            const Icon = tool.icon;
            const catInfo = CATEGORIES.find(c => c.key === tool.category);
            return (
              <Link key={i} to={tool.path}
                className="group ciq-card hover:border-white/15 transition-all duration-200"
                style={{ animationDelay: `${i * 40}ms` }}
                data-testid={`tool-card-${i}`}>
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${tool.color}15` }}>
                    <Icon className="h-5 w-5" style={{ color: tool.color }} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-white font-semibold text-sm group-hover:text-[#4d7cfe] transition-colors truncate">{tool.name}</h3>
                      {tool.badge && (
                        <span className="shrink-0 text-[10px] font-medium px-1.5 py-0.5 rounded-full" style={{ background: `${tool.color}15`, color: tool.color }}>{tool.badge}</span>
                      )}
                    </div>
                    <p className="text-[#64748b] text-xs mt-1 line-clamp-2">{tool.desc}</p>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded-full" style={{ background: `${catInfo?.color}12`, color: catInfo?.color, border: `1px solid ${catInfo?.color}25` }}>
                      {catInfo?.label}
                    </span>
                    <span className="text-[10px] text-[#64748b]">{tool.uses} uses</span>
                  </div>
                  <span className="text-xs text-[#4d7cfe] opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                    Try It <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {/* Category Quick Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-12">
        {Object.entries(CAT_DESCRIPTIONS).map(([key, info]) => {
          const cat = CATEGORIES.find(c => c.key === key);
          return (
            <button key={key} onClick={() => setActiveCategory(key)}
              className="ciq-card py-4 text-center hover:border-white/15 transition-all cursor-pointer" data-testid={`cat-stat-${key}`}>
              <p className="text-sm font-semibold" style={{ color: cat?.color }}>{cat?.label}</p>
              <p className="text-xs text-[#64748b] mt-1">{info.tagline}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}
